
import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4it0l7b8l4gait66h0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Get Your Free <strong>Estimate</strong>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Ready to start your construction project? Contact us today for a free consultation and detailed estimate.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Contact Form */}
          <div className="bg-gray-50 p-6 sm:p-8 rounded-2xl">
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="mb-5 sm:mb-6">
                <label htmlFor="name" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div className="mb-5 sm:mb-6">
                <label htmlFor="email" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div className="mb-5 sm:mb-6">
                <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm"
                  placeholder="(502) 533-0524"
                />
              </div>

              <div className="mb-5 sm:mb-6">
                <label htmlFor="service" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                  Service Needed *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent pr-8 text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="new-construction">New Construction</option>
                  <option value="remodeling">Remodeling</option>
                  <option value="custom-carpentry">Custom Carpentry</option>
                  <option value="roofing">Roofing Services</option>
                  <option value="decks-siding">Decks and Siding</option>
                  <option value="flooring-painting">Flooring and Painting</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-5 sm:mb-6">
                <label htmlFor="message" className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your project..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-red-700 transition-colors font-semibold text-base sm:text-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Request Free Estimate'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-lg text-sm sm:text-base">
                  Thank you! We'll contact you shortly to discuss your project.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg text-sm sm:text-base">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}
            </form>
          </div>

          {/* Contact Information */}
          <div>
            <div className="mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Contact Information</h3>
              
              <div className="space-y-5 sm:space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-fill text-xl sm:text-2xl text-red-600 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Phone</h4>
                    <a href="tel:502-533-0524" className="text-sm sm:text-base text-gray-600 hover:text-red-600 transition-colors cursor-pointer">
                      502-533-0524
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-fill text-xl sm:text-2xl text-red-600 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Email</h4>
                    <a href="mailto:info@ddrconstructionsolutions.com" className="text-sm sm:text-base text-gray-600 hover:text-red-600 transition-colors cursor-pointer break-all">
                      info@ddrconstructionsolutions.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-fill text-xl sm:text-2xl text-red-600 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Business Hours</h4>
                    <p className="text-sm sm:text-base text-gray-600">
                      Monday - Friday: 7:00 AM - 6:00 PM<br />
                      Saturday: 8:00 AM - 4:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-6 sm:p-8 rounded-2xl">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Why Request an Estimate?</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 text-lg sm:text-xl mr-3 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-gray-700">Free, no-obligation consultation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 text-lg sm:text-xl mr-3 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-gray-700">Detailed project assessment</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 text-lg sm:text-xl mr-3 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-gray-700">Transparent pricing breakdown</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 text-lg sm:text-xl mr-3 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-gray-700">Expert recommendations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 text-lg sm:text-xl mr-3 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-sm sm:text-base text-gray-700">Fast response within 24 hours</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
